import { ApiError } from "@models/error";
import type { ApiErrorResponse } from "@models/error";
import { getAccessTokenSync } from "./tokenStore";

export interface HttpClientOptions extends RequestInit {
  baseUrl?: string;
}

// Global token accessor - set by ContextProvider when BeeHive auth is ready
let globalGetAccessToken: (() => Promise<string | null>) | null = null;

export const setGlobalAccessTokenFn = (
  fn: () => Promise<string | null>,
): void => {
  globalGetAccessToken = fn;
};

export class HttpClient {
  private baseUrl: string;
  private getAccessTokenFn?: () => Promise<string | undefined>;

  constructor(
    baseUrl: string = "",
    getAccessTokenFn?: () => Promise<string | undefined>,
  ) {
    this.baseUrl = baseUrl;
    this.getAccessTokenFn = getAccessTokenFn;
  }

  private async getAccessToken(): Promise<string | null> {
    try {
      // Use instance-level token function if provided
      if (this.getAccessTokenFn) {
        const token = await this.getAccessTokenFn();
        if (token) return token;
      }
      // Fall back to global token accessor (set by BeeHive auth)
      if (globalGetAccessToken) {
        const token = await globalGetAccessToken();
        if (token) return token;
      }
      // Fall back to the in-memory store, never to localStorage: a
      // bearer token there is readable by any script on the page.
      return getAccessTokenSync();
    } catch (error) {
      console.error("Error getting access token:", error);
      return getAccessTokenSync();
    }
  }

  public async makeAuthenticatedRequest<T>(
    endpoint: string,
    options: HttpClientOptions = {},
    returnRawResponse: boolean = false,
  ): Promise<T> {
    const token = await this.getAccessToken();

    if (!token) {
      throw new Error("No access token available. Please log in.");
    }
    const { baseUrl, ...fetchOptions } = options;
    const url = (baseUrl || this.baseUrl) + endpoint;

    const isFormData = fetchOptions.body instanceof FormData;
    const defaultHeaders: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };
    if (!isFormData) {
      defaultHeaders["Content-Type"] = "application/json";
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers: {
        ...defaultHeaders,
        ...(fetchOptions.headers as Record<string, string>),
      },
    });

    if (returnRawResponse) {
      return response as unknown as T;
    }

    if (response.status === 401) {
      throw new Error("Authentication failed. Please log in again.");
    }

    if (!response.ok) {
      await this.handleErrorResponse(response);
    }

    return this.parseResponse<T>(response);
  }

  public async makeRequest<T>(
    endpoint: string,
    options: HttpClientOptions = {},
  ): Promise<T> {
    const { baseUrl, ...fetchOptions } = options;
    const url = (baseUrl || this.baseUrl) + endpoint;

    const response = await fetch(url, {
      ...fetchOptions,
      headers: {
        "Content-Type": "application/json",
        ...fetchOptions.headers,
      },
    });

    if (!response.ok) {
      await this.handleErrorResponse(response);
    }

    return this.parseResponse<T>(response);
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get("content-type");
    const contentLength = response.headers.get("content-length");

    if (response.status === 204 || contentLength === "0") {
      return undefined as T;
    }

    if (!contentType?.includes("application/json")) {
      const text = await response.text();
      if (!text) {
        return undefined as T;
      }
      try {
        return JSON.parse(text);
      } catch {
        return undefined as T;
      }
    }

    try {
      return await response.json();
    } catch (error) {
      console.error("Error parsing JSON response:", error);
      return undefined as T;
    }
  }

  private async handleErrorResponse(response: Response): Promise<never> {
    try {
      const contentType = response.headers.get("content-type");

      if (contentType && contentType.includes("application/json")) {
        const errorData = (await response.json()) as ApiErrorResponse;

        if (errorData.error_code && errorData.message) {
          throw ApiError.fromResponse(errorData, response.status);
        }
        // Core's newer envelope: {"detail": {"code", "message"}}.
        const detail = errorData.detail as { code?: unknown; message?: unknown } | undefined;
        if (detail && typeof detail.code === "string" && typeof detail.message === "string") {
          // The whole detail is kept: some errors carry data (e.g. the roles
          // still holding a permission in a 409 PERMISSION_IN_USE).
          throw new ApiError(detail.message, detail.code, response.status, undefined,
                             detail as Record<string, unknown>);
        }
        // Core's service-error envelope: {"error": "ValidationError", "message": "..."}.
        const plain = errorData as unknown as { error?: unknown; message?: unknown };
        if (typeof plain.error === "string" && typeof plain.message === "string") {
          throw new ApiError(plain.message, plain.error, response.status);
        }
      }

      const errorText = await response.text();
      throw new Error(
        `API request failed: ${response.status} ${
          errorText || response.statusText
        }`,
      );
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new Error(
        `API request failed: ${response.status} ${response.statusText}`,
      );
    }
  }
}
